import { getTrackingStep } from "~/shared/utils/tracking-status.ts";

import * as styles from "./shipment-details.module.css";

type ShipmentDetailsProps = {
	readonly mode: string | null;
	readonly poNumber: number;
	readonly latestStatus: string | null;
	readonly firstPickup: string | null;
	readonly finalDrop: string | null;
	readonly shipDate: string | null;
	readonly deliveryDate: string | null;
	readonly nextAppointment: string | null;
	readonly proNumber: string | null;
	readonly shipper: string | null;
	readonly receiver: string | null;
};

function formatDate(value: string | null): string {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()}`;
}

export function ShipmentDetails({
	mode,
	poNumber,
	latestStatus,
	firstPickup,
	finalDrop,
	shipDate,
	deliveryDate,
	nextAppointment,
	proNumber,
	shipper,
	receiver,
}: ShipmentDetailsProps) {
	const isLtl = mode?.toUpperCase() === "LTL";
	const trackingStep = getTrackingStep(latestStatus);
	const isPickedUp = trackingStep !== "Tendered";
	const isDelivered = trackingStep === "Delivered";

	return (
		<div data-testid={"shipment-details-section"}>
			<h2 className={styles.shipmentDetailsTitle}>Shipment Details</h2>
			<div className={styles.shipmentDetailsRow}>
				<div className={styles.shipmentDetailsField}>
					<span className={styles.shipmentDetailsLabel}>Mode</span>
					<span className={styles.shipmentDetailsValue}>{mode ?? "-"}</span>
				</div>
				<div className={styles.shipmentDetailsField}>
					<span className={styles.shipmentDetailsLabel}>TQL PO</span>
					<span className={styles.shipmentDetailsValue}>{poNumber}</span>
				</div>
				<div className={styles.shipmentDetailsField}>
					<span className={styles.shipmentDetailsLabel}>
						{isPickedUp ? "Picked Up On" : "Estimated Pickup"}
					</span>
					<span className={styles.shipmentDetailsValue}>
						{formatDate(shipDate)}
					</span>
				</div>

				<div className={styles.shipmentDetailsField}>
					<span className={styles.shipmentDetailsLabel}>
						{isDelivered ? "Delivered On" : "Estimated Delivery"}
					</span>
					<span className={styles.shipmentDetailsValue}>
						{formatDate(deliveryDate)}
					</span>
				</div>

				{isLtl ? (
					<div className={styles.shipmentDetailsField}>
						<span className={styles.shipmentDetailsLabel}>BOL#</span>
						<span className={styles.shipmentDetailsValue}>
							{proNumber ?? "-"}
						</span>
					</div>
				) : null}
				<div className={styles.shipmentDetailsField}>
					<span className={styles.shipmentDetailsLabel}>Status</span>
					<span className={styles.shipmentDetailsValue}>
						{latestStatus ?? "-"}
					</span>
				</div>
				{isLtl ? (
					<>
						<div className={styles.shipmentDetailsField}>
							<span className={styles.shipmentDetailsLabel}>Shipper</span>
							<span className={styles.shipmentDetailsValue}>
								{shipper ?? "-"}
							</span>
						</div>
						<div className={styles.shipmentDetailsField}>
							<span className={styles.shipmentDetailsLabel}>Receiver</span>
							<span className={styles.shipmentDetailsValue}>
								{receiver ?? "-"}
							</span>
						</div>
					</>
				) : (
					<>
						<div className={styles.shipmentDetailsField}>
							<span className={styles.shipmentDetailsLabel}>First Pickup</span>
							<span className={styles.shipmentDetailsValue}>
								{firstPickup ?? "-"}
							</span>
						</div>
						<div className={styles.shipmentDetailsField}>
							<span className={styles.shipmentDetailsLabel}>Final Drop</span>
							<span className={styles.shipmentDetailsValue}>
								{finalDrop ?? "-"}
							</span>
						</div>
					</>
				)}
				<div className={styles.shipmentDetailsField}>
					<span className={styles.shipmentDetailsLabel}>Next Appointment</span>
					<span className={styles.shipmentDetailsValue}>
						{nextAppointment ?? "-"}
					</span>
				</div>
			</div>
		</div>
	);
}
